import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

type Rating = { conceptId: string; difficulty: number };

export function useRatings() {
  const { token } = useAuth();
  const [byId, setById] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api<Rating[]>('/concepts/ratings', { token })
      .then((list) => setById(Object.fromEntries(list.map((r) => [r.conceptId, r.difficulty]))))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load difficulty ratings'));
  }, [token]);

  const rate = useCallback(async (conceptId: string, difficulty: number | null) => {
    setError(null);
    try {
      if (difficulty === null) {
        await api(`/concepts/ratings/${conceptId}`, { method: 'DELETE', token });
        setById((cur) => { const next = { ...cur }; delete next[conceptId]; return next; });
      } else {
        const saved = await api<Rating>(`/concepts/ratings/${conceptId}`, { method: 'PUT', token, body: { difficulty } });
        setById((cur) => ({ ...cur, [saved.conceptId]: saved.difficulty }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the difficulty rating');
    }
  }, [token]);

  const difficultyOf = useCallback((id: string): number | null => byId[id] ?? null, [byId]);

  return { difficultyOf, rate, error };
}
